import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Select } from "antd";
import { ViewMode } from "@thepocman/gantt-task-react";
import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { apiFetchJson } from "@/utils/apiFetch";
import TimelineLibrary from "@/components/Timeline/TimelineLibrary";
import TimelineHeaderControls from "@/components/Timeline/TimelineHeaderControls";
import { useTimelineControls } from "@/components/Timeline/useTimelineControls";
import type { TimelineItem } from "@/components/Timeline/timeline";
import {
  getEarliestTimelineDate,
  getLatestTimelineDate,
} from "@/components/Timeline/timelineSortUtils";
import { FALLBACK_COLUMN_WIDTH } from "@/components/Timeline/timelineUtils";
import type { TimelineUtils } from "@/components/Timeline/timelineUtils";
import type { MasteredSite } from "../types";
import type { TrialInfo } from "@/views/Portfolio/types";

type SiteMilestoneRow = {
  trial_id: string;
  site_status: string | null;
  site_selected_date: string | null;
  psv_date: string | null;
  siv_date: string | null;
  pi_start_date: string | null;
  pi_end_date: string | null;
  irb_submission_date: string | null;
  irb_approval_date: string | null;
  first_subject_consented: string | null;
  first_subject_enrolled: string | null;
  last_subject_last_visit: string | null;
  active_agreement: string | null;
  source_system: string | null;
};

const MILESTONE_FIELDS: {
  key: keyof SiteMilestoneRow;
  label: string;
  category: string;
}[] = [
  { key: "site_selected_date", label: "Site Selected", category: "Selection" },
  { key: "irb_submission_date", label: "IRB Submitted", category: "IRB" },
  { key: "irb_approval_date", label: "IRB Approved", category: "IRB" },
  { key: "psv_date", label: "Pre-Site Visit", category: "Activation" },
  { key: "siv_date", label: "Site Initiation Visit", category: "Activation" },
  { key: "pi_start_date", label: "PI Start", category: "PI" },
  {
    key: "first_subject_consented",
    label: "First Consented",
    category: "Enrollment",
  },
  {
    key: "first_subject_enrolled",
    label: "First Enrolled",
    category: "Enrollment",
  },
  { key: "pi_end_date", label: "PI End", category: "PI" },
  {
    key: "last_subject_last_visit",
    label: "Last Subject Last Visit",
    category: "Enrollment",
  },
];

// Map category → Gantt bar color
const CATEGORY_FILL: Record<string, string> = {
  Selection: "#FDE68A",
  IRB: "#BFDBFE",
  Activation: "#BBF7D0",
  PI: "#E9D5FF",
  Enrollment: "#FED7AA",
};
const CATEGORY_BORDER: Record<string, string> = {
  Selection: "#D97706",
  IRB: "#2563EB",
  Activation: "#16A34A",
  PI: "#7C3AED",
  Enrollment: "#EA580C",
};

const SITE_MILESTONES_COLUMN_WIDTHS: Record<string, number> = {
  trial: 140,
  event_name: 200,
  start_date: 110,
};

const siteMilestonesUtils: TimelineUtils = {
  defaultVisibleColumns: ["trial", "event_name", "start_date"],
  columnLabels: { trial: "Trial", event_name: "Milestone", start_date: "Date" },
  getTableColumnWidth: (col) =>
    SITE_MILESTONES_COLUMN_WIDTHS[col] ?? FALLBACK_COLUMN_WIDTH,
  getLegendFillColor: (val) => CATEGORY_FILL[val] ?? "#E5E7EB",
  getLegendBorderColor: (val) => CATEGORY_BORDER[val] ?? "#9CA3AF",
  getLegendBorderColorFromFill: (fill) => {
    const entry = Object.entries(CATEGORY_FILL).find(
      ([, v]) => v.toLowerCase() === fill.toLowerCase(),
    );
    return entry ? (CATEGORY_BORDER[entry[0]] ?? "#9CA3AF") : "#9CA3AF";
  },
  getAllLegendItems: (keys) => {
    const cats = keys?.length ? keys : Object.keys(CATEGORY_FILL);
    return cats.map((k) => ({
      key: k,
      label: k,
      fillColor: CATEGORY_FILL[k] ?? "#E5E7EB",
      borderColor: CATEGORY_BORDER[k] ?? "#9CA3AF",
    }));
  },
};

const TIMELINE_COLUMNS = [
  { fieldName: "trial", visibility: "visible" as const },
  { fieldName: "event_name", visibility: "visible" as const },
  { fieldName: "start_date", visibility: "visible" as const },
];

async function fetchSiteMilestones(
  scope: string,
  id: string,
  siteKey: string,
): Promise<SiteMilestoneRow[]> {
  return apiFetchJson<SiteMilestoneRow[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/site-milestones/${encodeURIComponent(siteKey)}/`,
  );
}

function rowsToTimelineItems(
  rows: SiteMilestoneRow[],
  trials: TrialInfo[],
): TimelineItem[] {
  const items: TimelineItem[] = [];
  for (const row of rows) {
    const trialLabel =
      trials.find((t) => t.id === row.trial_id)?.name ?? row.trial_id;
    for (const { key, label, category } of MILESTONE_FIELDS) {
      const date = row[key] as string | null;
      if (!date) continue;
      items.push({
        core_table: category,
        trial: trialLabel,
        event_type: category,
        event_name: label,
        start_date: date,
        end_date: date,
        ongoing_derived: null,
      });
    }
  }
  return items;
}

function toOptions(values: (string | null | undefined)[]) {
  return [...new Set(values.filter((v): v is string => !!v))]
    .sort()
    .map((value) => ({ value, label: value }));
}

type MilestonesTabProps = {
  site: MasteredSite;
  trials: TrialInfo[];
};

export default function MilestonesTab({ site, trials }: MilestonesTabProps) {
  const { scope, id } = useScopeParams();
  const isFlagEnabled = useIsFeatureFlagEnabled();
  const filterable = isFlagEnabled(FEATURE_FLAGS.PROFILE_SIMPLIFIED);

  const [trialFilter, setTrialFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

  const { data: rows = [], isLoading } = useQuery<SiteMilestoneRow[]>({
    queryKey: ["site-milestones", scope, id, site.site_key],
    queryFn: () => fetchSiteMilestones(scope!, id!, site.site_key),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const allItems = useMemo(
    () => rowsToTimelineItems(rows, trials),
    [rows, trials],
  );

  // A site that ran ten trials produces ten stacked milestone sets, which is
  // more rows than the Gantt can usefully show at once. Empty selections mean
  // "everything", so the unfiltered view is still the default.
  const timelineItems = useMemo(() => {
    if (!filterable) return allItems;
    return allItems.filter(
      (item) =>
        (trialFilter.length === 0 || trialFilter.includes(item.trial ?? "")) &&
        (categoryFilter.length === 0 ||
          categoryFilter.includes(item.event_type ?? "")),
    );
  }, [allItems, filterable, trialFilter, categoryFilter]);

  const trialOptions = useMemo(
    () => toOptions(allItems.map((item) => item.trial)),
    [allItems],
  );

  const categoryOptions = useMemo(
    () => toOptions(allItems.map((item) => item.event_type)),
    [allItems],
  );

  const firstDate = useMemo(
    () => getEarliestTimelineDate(timelineItems),
    [timelineItems],
  );
  const lastDate = useMemo(
    () => getLatestTimelineDate(timelineItems),
    [timelineItems],
  );

  const controls = useTimelineControls({
    firstEventDate: firstDate,
    initialViewMode: ViewMode.Year,
    initializeToFirstEvent: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Loading milestones…
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-[#8C8C8C]">
        <span className="text-[13px]">
          No milestone data available for this site.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {filterable && (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            mode="multiple"
            allowClear
            size="small"
            className="min-w-[240px] max-w-[420px]"
            placeholder="All trials"
            maxTagCount="responsive"
            value={trialFilter}
            options={trialOptions}
            onChange={setTrialFilter}
            data-testid="site-milestones-trial-filter"
          />
          <Select
            mode="multiple"
            allowClear
            size="small"
            className="min-w-[200px] max-w-[360px]"
            placeholder="All milestone types"
            maxTagCount="responsive"
            value={categoryFilter}
            options={categoryOptions}
            onChange={setCategoryFilter}
            data-testid="site-milestones-category-filter"
          />
          <span className="text-[12px] text-[#8C8C8C]">
            {timelineItems.length} of {allItems.length} milestones
          </span>
        </div>
      )}
      <TimelineHeaderControls
        viewMode={controls.viewMode}
        selectedDate={controls.selectedDate}
        isTodayActive={controls.isTodayActive}
        isFirstEventActive={controls.isFirstEventActive}
        hasFirstEventDate={Boolean(firstDate)}
        minSelectableDate={firstDate}
        maxSelectableDate={lastDate}
        testIdPrefix="site-milestones-timeline"
        onViewModeChange={controls.handleViewModeChange}
        onDateChange={controls.handleDateChange}
        onTodayClick={controls.handleTodayClick}
        onEarliestClick={controls.handleEarliestClick}
      />

      <TimelineLibrary
        data={timelineItems}
        viewMode={controls.viewMode}
        viewDate={controls.viewDate}
        centerOnDate={controls.centerOnDate}
        centerOnDateRequestId={controls.centerOnDateRequestId}
        columns={TIMELINE_COLUMNS}
        utils={siteMilestonesUtils}
        viewportOffset={260}
        onHorizontalScroll={controls.handleHorizontalScroll}
        onRowSelect={(item) =>
          controls.handleDateChange(dayjs(item.start_date))
        }
      />
    </div>
  );
}
